import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import CreateEventButton from '../components/CreateEventButton';
import { 
  Calendar, 
  DollarSign, 
  Clock, 
  Plus,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Ticket,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: 'bg-slate-500/20 text-muted-foreground border-slate-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
  completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [ticketGroups, setTicketGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, invoicesRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/my-tickets`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { ticket_groups: [] } })),
      ]);
      setEvents(eventsRes.data || []);
      setInvoices(invoicesRes.data || []);
      setTicketGroups(ticketsRes.data?.ticket_groups || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Tickets bought from hosted events (separate from "My Bookings" which are
  // self-planned client events). Surface them prominently — the #1 reason
  // returning customers couldn't find their comp/ticket purchases was that
  // this dashboard never mentioned them.
  const upcomingTicketGroups = ticketGroups
    .filter(g => g.event?.event_date && new Date(g.event.event_date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.event.event_date) - new Date(b.event.event_date));
  const totalTicketCount = ticketGroups.reduce((s, g) => s + (g.tickets?.length || 0), 0);

  const totalSpent = invoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + i.total, 0);

  const upcomingEvents = events.filter(e => 
    new Date(e.event_date) > new Date() && e.status !== 'cancelled'
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background pt-20 pb-12" data-testid="client-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            Manage your events and track your bookings
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-bold text-foreground">{events.length}</p>
                </div>
                <div className="p-3 rounded-xl bg-indigo-500/10">
                  <Calendar className="h-6 w-6 text-indigo-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Upcoming</p>
                  <p className="text-2xl font-bold text-foreground">{upcomingEvents.length}</p>
                </div>
                <div className="p-3 rounded-xl bg-lime-500/10">
                  <Clock className="h-6 w-6 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="text-2xl font-bold text-foreground">${totalSpent.toFixed(2)}</p>
                </div>
                <div className="p-3 rounded-xl bg-purple-500/10">
                  <DollarSign className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border cursor-pointer hover:border-indigo-500/60 transition-colors" onClick={() => navigate('/my-tickets')} data-testid="dashboard-tickets-stat">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">My Tickets</p>
                  <p className="text-2xl font-bold text-foreground">{totalTicketCount}</p>
                  {upcomingTicketGroups.length > 0 && (
                    <p className="text-[11px] text-indigo-400 mt-0.5">{upcomingTicketGroups.length} upcoming →</p>
                  )}
                </div>
                <div className="p-3 rounded-xl bg-indigo-500/10">
                  <Ticket className="h-6 w-6 text-indigo-400" />
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Upcoming Tickets — surfaces tickets bought from hosted events so
            returning customers (esp. comp recipients) can find them without
            digging through their email. */}
        {upcomingTicketGroups.length > 0 && (
          <Card className="bg-card border-border mb-8" data-testid="dashboard-upcoming-tickets">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Ticket className="h-5 w-5 text-indigo-400" /> Upcoming Tickets
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/my-tickets')} data-testid="dashboard-view-all-tickets">
                View all <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {upcomingTicketGroups.slice(0, 3).map((g, i) => {
                const ev = g.event || {};
                return (
                  <button
                    key={g.order?.order_id || i}
                    onClick={() => navigate('/my-tickets')}
                    className="w-full text-left p-3 rounded-lg border border-border hover:border-indigo-500/50 hover:bg-muted/40 transition-colors flex items-center gap-3"
                    data-testid={`dashboard-ticket-row-${i}`}
                  >
                    <div className="rounded-lg bg-indigo-500/10 p-2 flex-shrink-0">
                      <Ticket className="h-4 w-4 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-foreground truncate">{ev.title || ev.name || 'Event'}</p>
                      <p className="text-xs text-muted-foreground flex flex-wrap gap-3 mt-0.5">
                        {ev.event_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(ev.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
                        {ev.venue_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.venue_name}</span>}
                      </p>
                    </div>
                    <Badge className="bg-indigo-500/20 text-indigo-300 text-[10px]">
                      {g.tickets?.length || 0} ticket{(g.tickets?.length || 0) === 1 ? '' : 's'}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                );
              })}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Events */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-foreground">Your Events</h2>
              <Button 
                onClick={() => navigate('/calculator')}
                className="bg-indigo-500 hover:bg-indigo-600"
                data-testid="new-event-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Event
              </Button>
            </div>

            {events.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                    No events yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Start planning your first event with our pricing calculator
                  </p>
                  <Button onClick={() => navigate('/calculator')}>
                    Create Your First Event
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {events.map(event => (
                  <Card 
                    key={event.event_id} 
                    className="bg-card border-border hover:border-indigo-500/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/events/${event.event_id}`)}
                    data-testid={`event-card-${event.event_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-heading font-semibold text-foreground">{event.title}</h3>
                            <Badge className={STATUS_COLORS[event.status]}>
                              {event.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(event.event_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <span>{event.guest_count} guests</span>
                            <span>{event.selected_items?.length || 0} services</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-foreground">
                            ${event.total_price?.toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground">Estimated</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar - Recent Invoices */}
          <div className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Recent Invoices</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {invoices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No invoices yet
                  </p>
                ) : (
                  invoices.slice(0, 5).map(invoice => (
                    <div 
                      key={invoice.invoice_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer"
                      onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}
                    >
                      <div className="flex items-center space-x-3">
                        {invoice.status === 'paid' ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            #{invoice.invoice_id.slice(-8)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(invoice.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className="font-semibold text-foreground">
                        ${invoice.total.toFixed(2)}
                      </span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/calculator')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Plan New Event
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/marketplace')}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Browse Vendors
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
